import type { JourneyStage } from "@/lib/validation";

interface FunnelChartProps {
  stages: JourneyStage[];
  emptyMessage?: string;
}

function cleanLabel(label: string): string {
  return label.replace(/^\d+\.\s*/, "");
}

function formatStepChange(dropoff: number | null): {
  text: string;
  className: string;
} {
  if (dropoff === null) {
    return { text: "—", className: "text-fg-faint" };
  }
  if (dropoff === 0) {
    return { text: "0%", className: "text-fg-faint" };
  }
  if (dropoff > 0) {
    return {
      text: `−${dropoff.toFixed(0)}%`,
      className: "text-danger-text",
    };
  }
  return {
    text: `+${Math.abs(dropoff).toFixed(0)}%`,
    className: "text-warning-text",
  };
}

function formatConnector(dropoff: number | null): string | null {
  if (dropoff === null || dropoff === 0) return null;
  if (dropoff > 0) {
    return `↓ ${dropoff.toFixed(0)}% drop from previous step`;
  }
  return `↑ ${Math.abs(dropoff).toFixed(0)}% vs previous step (non-sequential counts)`;
}

function formatOverallSummary(
  sorted: JourneyStage[],
  baseline: number,
): string | null {
  if (sorted.length < 2) return null;

  const finalStage = sorted.at(-1)!;
  const finalSessions = finalStage.sessions;
  const finalLabel = cleanLabel(finalStage.label).toLowerCase();

  if (finalSessions > baseline) {
    return `Overall: final step exceeds top-of-funnel count (${finalSessions.toLocaleString("en-US")} vs ${baseline.toLocaleString("en-US")} from homepage)`;
  }

  const overallPct = (finalSessions / baseline) * 100;
  return `Overall: ${overallPct.toFixed(0)}% reached final step (${finalSessions.toLocaleString("en-US")} of ${baseline.toLocaleString("en-US")} from homepage — ${finalLabel})`;
}

export function FunnelChart({
  stages,
  emptyMessage = "No funnel data in the selected range",
}: FunnelChartProps) {
  if (stages.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border-subtle px-3 py-8 text-center text-sm text-fg-faint">
        {emptyMessage}
      </div>
    );
  }

  const sorted = [...stages].sort((a, b) => a.order - b.order);
  const baseline = Math.max(1, sorted[0].sessions);
  const overallSummary = formatOverallSummary(sorted, baseline);

  return (
    <div className="flex flex-col gap-3">
      <div
        className="grid grid-cols-[1fr_3.5rem_4rem_3.5rem] gap-x-3 text-[11px] uppercase tracking-wide text-fg-faint"
        aria-hidden
      >
        <div>Stage</div>
        <div className="text-right">% top</div>
        <div className="text-right">Sessions</div>
        <div className="text-right">vs prev</div>
      </div>

      <ol className="flex flex-col gap-2">
        {sorted.map((stage, index) => {
          const pctOfTop = (stage.sessions / baseline) * 100;
          const barWidth = Math.min(100, pctOfTop);
          const isOverflow = stage.sessions > baseline;
          const displayLabel = `${index + 1}. ${cleanLabel(stage.label)}`;
          const stepChange = formatStepChange(stage.dropoffFromPrevPct);
          const nextStage = sorted[index + 1];
          const connectorText = nextStage
            ? formatConnector(nextStage.dropoffFromPrevPct)
            : null;

          return (
            <li key={stage.name} className="flex flex-col gap-1">
              <div className="grid grid-cols-[1fr_3.5rem_4rem_3.5rem] items-baseline gap-x-3 text-xs">
                <div
                  className="truncate text-neutral-text-strong"
                  title={`${stage.label} (${stage.name})`}
                >
                  {displayLabel}
                </div>
                <div className="text-right tabular-nums text-fg-muted">
                  {pctOfTop.toFixed(0)}%
                </div>
                <div className="text-right tabular-nums text-neutral-text-strong">
                  {stage.sessions.toLocaleString("en-US")}
                </div>
                <div
                  className={`text-right text-[11px] tabular-nums ${stepChange.className}`}
                >
                  {stepChange.text}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div
                  className="h-4 min-w-0 flex-1 overflow-hidden rounded-md bg-info/10"
                  role="progressbar"
                  aria-label={`${displayLabel}: ${stage.sessions.toLocaleString("en-US")} sessions, ${pctOfTop.toFixed(0)}% of top`}
                  aria-valuenow={stage.sessions}
                  aria-valuemin={0}
                  aria-valuemax={baseline}
                >
                  <div
                    className="h-full rounded-md bg-info/70"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
                {isOverflow ? (
                  <span className="shrink-0 text-[10px] tabular-nums text-warning-text">
                    &gt;100% of top
                  </span>
                ) : null}
              </div>

              {connectorText ? (
                <p className="py-0.5 text-center text-[11px] text-fg-faint">
                  {connectorText}
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>

      {overallSummary ? (
        <p className="border-t border-border-subtle pt-2 text-xs text-fg-muted">
          {overallSummary}
        </p>
      ) : null}
    </div>
  );
}
